import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

@Injectable()
export class ApiService {
  api_domain = 'https://api.lbets.io';
  constructor(public http: HttpClient) {}

  /**
   * Sends a GET request to the specified URI.
   * @param uri - The URI to send the GET request to.
   * @returns An observable of the response.
   */
  get(uri: string): Observable<any> {
    const domain = window.location.hostname;
    return this.http.get(this.parseUri(uri), {
      headers: {
        'Content-Type': 'application/json',
        'x-domain': domain,
      },
    });
  }

  /**
   * Sends a POST request to the specified URI with the given parameters.
   * @param uri - The URI to send the POST request to.
   * @param params - The parameters to include in the POST request.
   * @param options - Optional additional options for the request.
   * @returns An observable of the response.
   */
  post(uri: string, params: any, options?: any): Observable<any> {
    const _options = {
      ...options,
      headers: { 'Content-Type': 'application/json', Origin: 'lbets.io' },
    };
    return this.http.post(this.parseUri(uri), JSON.stringify(params), _options);
  }
  /**
   * Sends a POST request with FormData to the specified URI.
   * @param uri - The URI to send the POST request to.
   * @param formData - The FormData object to send
   * @param options - Optional additional options for the request
   * @returns An observable of the response
   */
  postFormData(uri: string, formData: FormData, options?: any): Observable<any> {
    const _options = {
      ...options,
      reportProgress: true,
      observe: 'events',
      headers: {
        Accept: 'application/json',
      },
    };
    return this.http.post(this.parseUri(uri), formData, _options);
  }

  /**
   * Sends a PATCH request to the specified URI with the given parameters.
   * @param uri - The URI to send the PATCH request to.
   * @param params - The parameters to include in the PATCH request.
   * @returns An observable of the response.
   */
  patch(uri: string, params = {}): Observable<any> {
    return this.http.patch(this.parseUri(uri), JSON.stringify(params), {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Sends a PUT request to the specified URI with the given parameters.
   * @param uri - The URI to send the PUT request to.
   * @param params - The parameters to include in the PUT request.
   * @returns An observable of the response.
   */
  put(uri: string, params = {}): Observable<any> {
    return this.http.put(this.parseUri(uri), JSON.stringify(params), {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Sends a DELETE request to the specified URI.
   * @param uri - The URI to send the DELETE request to.
   * @returns An observable of the response.
   */
  delete(uri: string): Observable<any> {
    return this.http.delete(this.parseUri(uri));
  }

  /**
   * Sends a GET request to retrieve a file from the specified URI.
   * @param uri - The URI to send the GET request to.
   * @returns An observable of the file content as a string.
   */
  getFile(uri: string): Observable<string> {
    return this.http.get(this.parseUri(uri), { responseType: 'text' });
  }
  /**
   * Sends a GET request to retrieve a file from the specified URI as an ArrayBuffer.
   * @param uri - The URI to send the GET request to.
   * @returns An observable of the file content as an ArrayBuffer.
   */
  getFileAsBlob(uri: string): Observable<any> {
    return this.http
      .get(this.parseUri(uri), { responseType: 'arraybuffer' })
      .pipe(map((arrayBuffer: ArrayBuffer) => URL.createObjectURL(new Blob([arrayBuffer]))));
  }
  getAnimationAsBlob(uri: string): Observable<any> {
    return this.http.get(this.parseUri(uri), { responseType: 'arraybuffer' }).pipe(
      map((arrayBuffer: ArrayBuffer) => {
        const blob = new Blob([arrayBuffer], { type: 'video/mp4' });
        const videoUrl = URL.createObjectURL(blob);
        return videoUrl;
      }),
    );
  }
  /**
   * Parses the given URI to include the base API URL.
   * @param uri - The URI to parse.
   * @returns The parsed URI with the base API URL.
   */
  parseUri(uri: string): string {
    return `${this.api_domain}/${uri}`;
  }
}
